import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { fmtRs } from "../../utils/payrollFormat";

const PRIMARY = "#1B5C20";

const otTypes = [
  { value: "T", label: "Normal OT" },
  { value: "H", label: "Holiday OT" },
  { value: "S", label: "Sunday OT" },
];

const initialForm = {
  employeeId: "",
  payrollPeriodId: "",
  otDate: new Date().toISOString().split("T")[0],
  otType: "T",
  startTime: "",
  endTime: "",
  hours: 0,
  rate: 0,
  amount: 0,
  isApproved: false,
  payrollId: "",
  remarks: "",
};

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
`;

function Field({ label, children }) {
  return (
    <div className="relative">
      <label className={labelClass}>
        {label}
      </label>

      {children}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  readOnly = false,
  helperText,
  helperError = false,
}) {
  return (
    <div>
      <Field label={label}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          className={`
            ${inputClass}
            ${readOnly ? "bg-surface-50" : ""}
            ${disabled ? "cursor-not-allowed bg-surface-50 text-surface-400" : ""}
          `}
        />
      </Field>

      {helperText && (
        <p
          className={`
            mt-1 px-1 text-[12px]
            ${helperError ? "text-[#C0392B]" : "text-surface-500"}
          `}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <Field label={label}>
      <input
        type="date"
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-none`}
      />
    </Field>
  );
}

function TimeInput({ label, value, onChange }) {
  return (
    <Field label={label}>
      <input
        type="time"
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-none`}
      />
    </Field>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  children,
}) {
  return (
    <Field label={label}>
      <select
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-auto cursor-pointer`}
      >
        {children}
      </select>
    </Field>
  );
}

function TextAreaInput({
  label,
  value,
  onChange,
  rows = 2,
}) {
  return (
    <Field label={label}>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        className="
          w-full
          rounded-md
          border border-surface-200
          bg-white
          px-3.5
          py-3
          text-[16px]
          text-surface-800
          outline-none
          transition-colors
          focus:border-primary-500
          focus:ring-1
          focus:ring-primary-500
        "
      />
    </Field>
  );
}

function CheckboxInput({
  checked,
  onChange,
  label,
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-[15px] text-surface-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="
          h-4 w-4
          cursor-pointer
          rounded
          border-surface-300
          accent-primary-600
          focus:ring-primary-500
        "
      />

      {label}
    </label>
  );
}

// Difference between two datetime-local values, in hours (2dp, never negative).
// const hoursBetween = (start, end) => {
//   if (!start || !end) return 0;
//   const startMs = new Date(start).getTime();
//   const endMs = new Date(end).getTime();
//   if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 0;
//   return Math.round(((endMs - startMs) / 3600000) * 100) / 100;
// };

const hoursBetween = (start, end) => {
  if (!start || !end) return 0;

  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  if (endMinutes <= startMinutes) return 0;

  return Math.round(((endMinutes - startMinutes) / 60) * 100) / 100;
};

const getFormattedDate = (date) => {
  if (!date) return;

  const raw = String(date).trim();

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.split(" ")[0];
  }

  // M/D/YYYY or MM/DD/YYYY
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);

  if (match) {
    const [, month, day, year] = match;

    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return "";
}

export default function AddOvertimeDialog({
  open,
  onClose,
  onSave,
  loading,
  employees = [],
  payrollPeriods = [],
  initialData,
}) {
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      console.log(initialData)

      setForm({
        id: initialData.id,
        employeeId: initialData.employeeId,
        payrollPeriodId: initialData.payrollPeriodId,
        otDate: getFormattedDate(initialData.date),
        otType: initialData.otType || "T",
        startTime: initialData.startTime,
        endTime: initialData.endTime,
        hours: initialData.hours,
        rate: initialData.rate || 0,
        amount: initialData.amount,
        isApproved: !!initialData.isApproved,
        approvedBy: initialData.approvedBy,
        payrollId: initialData.payrollId || "",
        createdBy: initialData.createdBy,
        isActive: initialData.isActive,
      });
    } else {
      setForm(initialForm);
    }
  }, [open, initialData]);

  // Recompute hours + amount whenever the inputs that drive them change.
  useEffect(() => {
    const hours = hoursBetween(form.startTime, form.endTime);
    const amount = Math.round(hours * (Number(form.rate) || 0) * 100) / 100;

    setForm((prev) => ({ ...prev, hours, amount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startTime, form.endTime, form.rate]);

  const handleChange = (field) => (e) => {
    const value = field === "isApproved" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    const employee = employees.find(
      (item) => String(item.HED_EMPLOYEE_ID) === String(employeeId)
    );

    setForm((prev) => ({
      ...prev,
      employeeId,
      rate: employee?.HED_OT_RATE ?? 0,
    }));
  };

  const handleSave = () => {
    onSave({
      ...form,
      isActive: true,
    });
  };

  const rateIsMissing = !form.rate || Number(form.rate) <= 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          {initialData ? "Update Overtime" : "New Overtime Entry"}
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Employee */}
          <SelectInput
            label="Employee"
            value={form.employeeId}
            onChange={handleEmployeeChange}
          >
            <option value="">
              Select Employee
            </option>

            {employees.map((emp) => (
              <option
                key={emp.HED_EMPLOYEE_ID}
                value={emp.HED_EMPLOYEE_ID}
              >
                {emp.HED_REPORT_NAME || "-"} ({emp.HED_SERVICE_NO})
              </option>
            ))}
          </SelectInput>

          {/* Payroll Period */}
          <SelectInput
            label="Payroll Period"
            value={form.payrollPeriodId}
            onChange={handleChange("payrollPeriodId")}
          >
            <option value="">
              Select Payroll Period
            </option>

            {payrollPeriods.map((period) => (
              <option
                key={period.id}
                value={period.id}
              >
                {period.code} - {period.name}
              </option>
            ))}
          </SelectInput>

          {/* OT Date */}
          <DateInput
            label="OT Date"
            value={form.otDate}
            onChange={handleChange("otDate")}
          />

          {/* OT Type */}
          <SelectInput
            label="OT Type"
            value={form.otType}
            onChange={handleChange("otType")}
          >
            {otTypes.map((type) => (
              <option
                key={type.value}
                value={type.value}
              >
                {type.label}
              </option>
            ))}
          </SelectInput>

          {/* <TextField
            fullWidth
            type="datetime-local"
            label="Start Time"
            InputLabelProps={{ shrink: true }}
            value={form.startTime}
            onChange={handleChange("startTime")}
          /> */}

          {/* Start Time */}
          <TimeInput
            label="Start Time"
            value={form.startTime}
            onChange={handleChange("startTime")}
          />

          {/* <TextField
            fullWidth
            type="datetime-local"
            label="End Time"
            InputLabelProps={{ shrink: true }}
            value={form.endTime}
            onChange={handleChange("endTime")}
          /> */}

          {/* End Time */}
          <TimeInput
            label="End Time"
            value={form.endTime}
            onChange={handleChange("endTime")}
          />

          {/* Total Hours */}
          <TextInput
            label="Total Hours"
            value={form.hours}
            readOnly
            helperText="Auto-calculated from start/end time"
          />

          {/* OT Rate */}
          <TextInput
            label="OT Rate (per hour)"
            value={form.rate}
            readOnly
            helperText={
              rateIsMissing
                ? "No OT rate is configured for this employee"
                : "Pulled from the employee record"
            }
            helperError={rateIsMissing}
          />

          {/* OT Amount */}
          <TextInput
            label="OT Amount"
            value={fmtRs(form.amount)}
            readOnly
            helperText="Hours × Rate"
          />

          {/* Approved */}
          <div className="flex items-center">
            <CheckboxInput
              checked={form.isApproved}
              onChange={handleChange("isApproved")}
              label="Approved"
            />
          </div>

          {/* Payroll ID */}
          {initialData && (
            <TextInput
              label="Payroll ID (optional)"
              value={form.payrollId}
              onChange={handleChange("payrollId")}
            />
          )}

          {/* Remarks */}
          <div className="sm:col-span-2">
            <TextAreaInput
              label="Remarks"
              value={form.remarks}
              onChange={handleChange("remarks")}
              rows={2}
            />
          </div>

          {/* Rate Warning */}
          {rateIsMissing && (
            <div className="sm:col-span-2">
              <div className="rounded-lg bg-[#FFF4E0] p-3">
                <p className="text-[12px] text-[#B26A00]">
                  No OT rate has been configured for the selected employee. The
                  amount can't be calculated without it.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          className="!text-primary-600"
        >
          CANCEL
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            loading ||
            rateIsMissing ||
            !form.employeeId ||
            !form.startTime ||
            !form.endTime
          }
          className="!bg-primary-600 hover:!bg-primary-700"
        >
          {loading ? "SAVING..." : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
